import { useCallback, useEffect, useMemo, useState } from "react";
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { Alert, Button, Checkbox, Drawer, Input, Modal, Space, Table, Tooltip, message } from "antd";
import { useSelector } from "react-redux";
import { serverFetch } from "@core/server";
import ChannelForm from "./channel";
import { dictsChannelVidName } from "../channel-vids";
import "./channels.css";

const errMsg = (e) => e?.message || "Ошибка";

const getLawFlag = (law, index) => {
  if (!law || law.length <= index) return 1;
  const n = Number(law[index]);
  return Number.isFinite(n) ? n : 1;
};

const Channels = () => {
  const law = useSelector((state) => state.user.session?.law);
  const canEdit = getLawFlag(law, 40) === 1;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerId, setDrawerId] = useState(null);

  const getData = useCallback(() => {
    setLoading(true);
    setError("");
    serverFetch("Message/Channel")
      .then((data) => {
        setItems(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        setError(errMsg(e));
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(getData, [getData]);

  const filtered = useMemo(
    () =>
      items.filter((item) => {
        if (!showHidden && !item.active) return false;
        const q = searchText.trim().toLowerCase();
        if (!q) return true;
        return `${item.name || ""} ${item.code || ""}`.toLowerCase().includes(q);
      }),
    [items, showHidden, searchText]
  );

  const openCreate = () => {
    if (!canEdit) return;
    setDrawerId(null);
    setDrawerOpen(true);
  };

  const openEdit = (row) => {
    if (row?.id == null) return;
    setSelectedId(row.id);
    setDrawerId(row.id);
    setDrawerOpen(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Удалить канал?",
      content: "Это действие нельзя отменить.",
      okText: "Удалить",
      okType: "danger",
      cancelText: "Отмена",
      onOk: () =>
        serverFetch(`Message/Channel/${record.id}`, { method: "DELETE" })
          .then(() => {
            message.success("Канал удалён");
            if (drawerId === record.id) setDrawerOpen(false);
            setSelectedId(null);
            return getData();
          })
          .catch((e) => {
            message.error(errMsg(e));
            return Promise.reject(e);
          })
    });
  };

  const columns = [
    { title: "#", dataIndex: "id", width: 70 },
    { title: "Наименование", dataIndex: "name" },
    { title: "Код", dataIndex: "code", width: 160, ellipsis: true },
    { title: "Вид", dataIndex: "vid", width: 140, render: dictsChannelVidName },
    { title: "Порядок", dataIndex: "idx", width: 80, align: "right" },
    {
      title: "Акт",
      dataIndex: "active",
      width: 56,
      align: "center",
      render: (v) => <Checkbox checked={!!v} disabled />
    }
  ];

  if (!canEdit) {
    return (
      <div className="msg-channels">
        <Alert type="warning" showIcon message="Нет права на настройки каналов" />
      </div>
    );
  }

  return (
    <div className="msg-channels">
      {error ? <Alert className="msg-channels__error" type="error" showIcon message={error} /> : null}
      <div className="msg-channels__toolbar">
        <Space size={4}>
          <Tooltip title="Добавить">
            <Button type="text" icon={<PlusOutlined />} onClick={openCreate} />
          </Tooltip>
          <Tooltip title="Удалить">
            <Button
              type="text"
              icon={<DeleteOutlined />}
              disabled={!selectedId}
              onClick={() => {
                const row = items.find((i) => i.id === selectedId);
                if (row) handleDelete(row);
              }}
            />
          </Tooltip>
          <Tooltip title="Обновить">
            <Button type="text" icon={<ReloadOutlined />} onClick={getData} />
          </Tooltip>
        </Space>
        <Input
          className="msg-channels__filter"
          placeholder="Поиск"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
        <Checkbox checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)}>
          Показывать неактивные
        </Checkbox>
      </div>
      <Table
        className="msg-channels__table"
        dataSource={filtered}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={false}
        onRow={(record) => ({
          onClick: () => setSelectedId(record.id),
          onDoubleClick: () => openEdit(record)
        })}
        rowClassName={(record) => (record.id === selectedId ? "msg-channels__row--selected" : "")}
      />
      <Drawer
        title={drawerId ? "Канал" : "Новый канал"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={640}
        destroyOnHidden
      >
        <ChannelForm
          id={drawerId}
          onClose={(refresh) => {
            setDrawerOpen(false);
            if (refresh) getData();
          }}
        />
      </Drawer>
    </div>
  );
};

export default Channels;
